// Bridge between the web app and the boring Chrome extension.
//
// The extension exposes a tiny message API through externally_connectable
// (see chrome-extension/background.js). The app mints a Firebase custom token
// for the signed-in user and hands it over, so the extension gets its own
// session without ever seeing a password.
import { firebaseAuth, generateCustomAuthToken, signUserInWithToken } from "./firebase";
import { isProduction } from "./config";

export const EXTENSION_ID = "iieaopbkckcahcghbhognjaglgdocdfj";
export const POST_LOGIN_PATH_KEY = "boring.postLoginPath";
export const CONNECT_EXTENSION_PATH = "/app/connect-extension";

const MESSAGES = {
  ping: "boring:ping",
  connect: "boring:connect",
  disconnect: "boring:disconnect",
  status: "boring:status",
  resolveLinkedInUrl: "boring:resolveLinkedInUrl",
  signInToken: "boring:signInToken",
  linkedinCard: "boring:linkedinCard",
};

export interface ExtensionSeat {
  hasSeat: boolean;
  source: "admin" | "license" | null;
  reason?: string;
}

export interface ExtensionContext {
  uid: string;
  email: string;
  isAdmin: boolean;
  organization: {
    id: string;
    name?: string;
    integrationType: "outreach" | "hubspot";
    isIntegrationAuthorized: boolean;
    isActive: boolean;
    inactiveReason?: string;
  } | null;
  seat: ExtensionSeat;
  features: Record<string, boolean>;
}

export interface ExtensionState {
  signedIn: boolean;
  email: string | null;
  environment: "production" | "staging";
  appUrl: string;
  context: ExtensionContext | null;
}

export interface ExtensionInfo {
  installed: boolean;
  version?: string;
  environment?: string;
}

interface ChromeRuntime {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response: unknown) => void
  ) => void;
  lastError?: { message?: string };
}

const getRuntime = (): ChromeRuntime | null => {
  const chromeGlobal = (
    window as unknown as { chrome?: { runtime?: ChromeRuntime } }
  ).chrome;
  return chromeGlobal?.runtime?.sendMessage ? chromeGlobal.runtime : null;
};

const sendToExtension = <T>(
  message: Record<string, unknown>,
  timeoutMs = 4000
): Promise<T> =>
  new Promise((resolve, reject) => {
    const runtime = getRuntime();
    if (!runtime) {
      reject(new Error("extension_unavailable"));
      return;
    }
    const timer = window.setTimeout(
      () => reject(new Error("extension_timeout")),
      timeoutMs
    );
    try {
      runtime.sendMessage(EXTENSION_ID, message, (response) => {
        window.clearTimeout(timer);
        if (runtime.lastError || response === undefined) {
          reject(
            new Error(runtime.lastError?.message || "extension_unavailable")
          );
          return;
        }
        resolve(response as T);
      });
    } catch (error) {
      window.clearTimeout(timer);
      reject(error);
    }
  });

export interface ResolvedLinkedInMember {
  urn: string;
  vanity: string;
  name: string | null;
}

/**
 * The member urn behind a public LinkedIn URL, read with the rep's own LinkedIn
 * session inside the extension. Null when the extension is missing, signed out
 * of LinkedIn, or the profile cannot be read.
 */
export const resolveLinkedInUrl = async (url: string): Promise<ResolvedLinkedInMember | null> => {
  if (!url) return null;
  try {
    const response = await sendToExtension<{ ok: boolean; urn?: string; vanity?: string; name?: string | null }>(
      { type: MESSAGES.resolveLinkedInUrl, url },
      8000
    );
    return response?.ok && response.urn ? { urn: response.urn, vanity: response.vanity || "", name: response.name || null } : null;
  } catch {
    return null;
  }
};

export type LinkedInCardAnswer<T> = { ok: true } & T;
export type LinkedInCardFailure = { ok: false; error?: string };
export type LinkedInCardResult<T> = LinkedInCardAnswer<T> | LinkedInCardFailure;

/**
 * A LinkedIn card read or action for the Outreach profile and activity tiles,
 * answered by the extension with the rep's own LinkedIn session (the same code
 * as the cards it injects). The extension looks up the prospect's LinkedIn URL
 * itself, from the prospect id. Never throws: a missing extension, a timeout
 * or LinkedIn saying no all come back as { ok: false, error }.
 */
export const linkedInCard = async <T>(
  message: { prospectId: string; part: string; requestId?: string; [key: string]: unknown },
  timeoutMs = 60000
): Promise<LinkedInCardResult<T>> => {
  try {
    const response = await sendToExtension<LinkedInCardResult<T>>({ type: MESSAGES.linkedinCard, ...message }, timeoutMs);
    return response && typeof response === "object" ? response : { ok: false, error: "extension_unavailable" };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "extension_unavailable" };
  }
};

let embeddedSignIn: Promise<boolean> | null = null;

/**
 * Signs this page in with the extension's session when it has none of its own.
 *
 * The Outreach surfaces (LinkedIn DMs tab, tile, inbox) run as a third-party
 * iframe inside web.outreach.io, and Chrome partitions that frame's storage:
 * a sign-in on app.boringplugins.com is invisible there, so every callable
 * went out without auth and failed. The extension is signed in as the same
 * rep, so it hands over a custom token. Resolves true once a user is signed in.
 */
export const ensureSignedInFromExtension = (): Promise<boolean> => {
  if (!embeddedSignIn) {
    embeddedSignIn = (async () => {
      await firebaseAuth.authStateReady();
      if (firebaseAuth.currentUser) return true;
      try {
        const response = await sendToExtension<{ ok: boolean; customToken?: string; error?: string }>(
          { type: MESSAGES.signInToken },
          15000
        );
        if (!response?.ok || !response.customToken) return false;
        await signUserInWithToken(response.customToken);
        return true;
      } catch {
        return false;
      }
    })().then((signedIn) => {
      // Let a later call try again, e.g. after the rep connects the extension.
      if (!signedIn) embeddedSignIn = null;
      return signedIn;
    });
  }
  return embeddedSignIn;
};

/** True when the browser is Chrome-like and the boring extension is installed. */
export const getExtensionInfo = async (): Promise<ExtensionInfo> => {
  try {
    const response = await sendToExtension<{
      ok: boolean;
      version?: string;
      environment?: string;
    }>({
      type: MESSAGES.ping,
    });
    return {
      installed: !!response?.ok,
      version: response?.version,
      environment: response?.environment,
    };
  } catch {
    return { installed: false };
  }
};

export const getExtensionState = async (): Promise<ExtensionState | null> => {
  try {
    const response = await sendToExtension<{
      ok: boolean;
      state?: ExtensionState;
    }>({ type: MESSAGES.status });
    return response?.ok ? response.state || null : null;
  } catch {
    return null;
  }
};

/** Mints a custom token for the signed-in user and signs the extension in with it. */
export const connectExtension = async (): Promise<ExtensionState> => {
  const customToken = await generateCustomAuthToken();
  const response = await sendToExtension<{
    ok: boolean;
    state?: ExtensionState;
    error?: string;
  }>(
    {
      type: MESSAGES.connect,
      customToken,
      environment: isProduction ? "production" : "staging",
    },
    15000
  );
  if (!response?.ok || !response.state) {
    throw new Error(response?.error || "extension_connect_failed");
  }
  return response.state;
};

export const disconnectExtension = async (): Promise<void> => {
  await sendToExtension({ type: MESSAGES.disconnect });
};

export const CHROME_WEB_STORE_URL = `https://chromewebstore.google.com/detail/${EXTENSION_ID}`;
